import { AcceptedUploadFileTypes, focusVisible, measures, strings, text } from "@quickpoint.me/shared";
import { useEffect, useRef } from "react";
import { useRecoilState } from "recoil";
import styled from "styled-components";
import { FileTypeToDisplay } from "../state/fileState";

/* One action row: Browse / Search / AI, three equal drop-zone buttons.
   A file dropped on any of them uploads (they all carry the drop-zone class,
   so the drop bubbles to the panel handler like the rest of the modal). */
const StyledRow = styled.div<{ $isInPortrait: boolean }>`
    display: flex;
    gap: 0.6rem;
    align-items: center;
    margin-inline: ${props =>
    props.$isInPortrait ? measures.dialogPanel.portrait.margin : measures.dialogPanel.landscape.margin};
`;

/* shared button base: equal thirds, original 48px height (WCAG 1.4.9 real
   text; #5335c9 is 7.7:1 on white — AAA). */
const buttonBase = `
    flex: 1 1 0;
    min-width: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 0.4rem;
    cursor: pointer;
    min-height: 48px;
    padding-inline: 0.9rem;
    /* match the shared StyledPillButton language (8px radius, 500 weight) */
    border-radius: 8px;
    font-weight: 500;
    font-size: 0.9rem;
    white-space: nowrap;
`;

/* Browse: the dashed drop zone. */
const StyledBrowse = styled.button.attrs({ type: "button" })`
    ${buttonBase}
    background: rgba(130, 101, 253, 0.03);
    border: 1.5px dashed #5335c9;
    color: #5335c9;
    font-family: ${props => props.theme.font};
    ${focusVisible}

    &:hover {
      background: #5335c9;
      color: #fff;
    }
`;

/* Search + AI: outline pills; the active one (its pane open) is filled.
   On audio/data they're hidden with visibility (not removed) so the layout —
   and the Browse drop zone's width — stays byte-identical, no jump. */
const StyledActionButton = styled.button.attrs({ type: "button" })<{ $active: boolean; $hidden: boolean }>`
    ${buttonBase}
    font-weight: 500;
    font-family: ${props => props.theme.font};
    border: 1.5px solid #5335c9;
    background: ${props => (props.$active ? "#5335c9" : "transparent")};
    color: ${props => (props.$active ? "#fff" : "#5335c9")};
    visibility: ${props => (props.$hidden ? "hidden" : "visible")};
    pointer-events: ${props => (props.$hidden ? "none" : "auto")};
    ${focusVisible}

    &:hover {
      background: #5335c9;
      color: #fff;
    }

    svg { width: 1.1rem; height: 1.1rem; }
`;

function BrowseButton({ $isInPortrait }: { $isInPortrait: boolean }) {
    const toAuthorBC = new BroadcastChannel(strings.broadcastChannel.author);
    const [fileTypeToDisplay, setFileTypeToDisplay] = useRecoilState(FileTypeToDisplay);
    const inputRef = useRef<HTMLInputElement>(null);

    const handleBrowseClick = () => {
        // if we're on a pane, return to the image grid before opening the picker
        if (fileTypeToDisplay !== "image") setFileTypeToDisplay("image");
        inputRef.current?.click();
    };

    const handleUpload = (e: Event) => {
        const dropZones = document.getElementsByClassName(strings.css.fileDropZone) as HTMLCollection;
        const dropEvent = new Event("drop", {
            bubbles: true,
            cancelable: true,
        });

        const files = (e.target as HTMLInputElement).files;

        (dropEvent as any).dataTransfer = {
            files,
        } as DataTransfer;
        dropZones[0].dispatchEvent(dropEvent);
    };

    useEffect(() => {
        if (inputRef.current) {
            inputRef.current.addEventListener("change", handleUpload);
        }

        return () => inputRef.current?.removeEventListener("change", handleUpload);
    });

    useEffect(() => {
        return () => toAuthorBC.close();
    });

    const accept = AcceptedUploadFileTypes.join(",");
    const dropZone = strings.css.fileDropZone;

    // Search and AI only make sense for images. On audio/data they hide, but
    // empty spacers hold their place so the Browse drop zone keeps the same
    // size (no jump to full width).
    const showImageActions =
        fileTypeToDisplay === "image" ||
        fileTypeToDisplay === "search" ||
        fileTypeToDisplay === "ai";

    return (
        <StyledRow $isInPortrait={$isInPortrait}>
            <StyledBrowse className={dropZone} onClick={handleBrowseClick}>
                {text.fileUploader.browse}
                <input ref={inputRef} type="file" accept={accept} multiple hidden />
            </StyledBrowse>
            <StyledActionButton
                className={showImageActions ? dropZone : undefined}
                $active={fileTypeToDisplay === "search"}
                $hidden={!showImageActions}
                aria-hidden={!showImageActions}
                tabIndex={showImageActions ? undefined : -1}
                aria-pressed={fileTypeToDisplay === "search"}
                onClick={() => setFileTypeToDisplay("search")}
            >
                {text.fileUploader.searchImagesOpen}
            </StyledActionButton>
            <StyledActionButton
                className={showImageActions ? dropZone : undefined}
                $active={fileTypeToDisplay === "ai"}
                $hidden={!showImageActions}
                aria-hidden={!showImageActions}
                tabIndex={showImageActions ? undefined : -1}
                aria-pressed={fileTypeToDisplay === "ai"}
                onClick={() => setFileTypeToDisplay("ai")}
            >
                {text.fileUploader.tabAi}
            </StyledActionButton>
        </StyledRow>
    );
}

export default BrowseButton;
